<!-- PlanningRow.vue -->
<template>
    <tr class="ppv2-group">
        <!-- Employee / Resource Identity Cell -->
        <td
            class="ppv2-group/cell ppv2-sticky ppv2-left-0 ppv2-z-[999] ppv2-bg-[#5a008711] ppv2-border-r ppv2-border-b ppv2-border-white ppv2-px-4 ppv2-py-3 ppv2-h-[72px] ppv2-relative"
            :class="{ '!ppv2-z-50': isMenuOpen }"
        >
            <div class="ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-h-full">
                <div class="ppv2-text-sm ppv2-text-black">
                    {{ !item.user ? item.name : `${item.user.first_name} ${item.user.last_name}` }}
                </div>
                <div class="ppv2-text-xs ppv2-text-black">
                    {{
                        !item.user
                            ? capitalizeFirst($t('staff_planning_v3.row.external_resource'))
                            : capitalizeFirst(item.user.role)
                    }}
                </div>
            </div>

            <!-- Contextual Dropdown (Only for official staff) -->
            <div class="ppv2-absolute ppv2-right-2 ppv2-top-1/2 ppv2--translate-y-1/2 ppv2-hidden group-hover/cell:ppv2-block">
                <DropdownMenu
                    v-if="menuItems.length"
                    :items="menuItems"
                    @toggle="isMenuOpen = $event"
                    @select="$emit('menu-select', $event)"
                >
                    <template #trigger>
                        <button
                            class="pppv2-w-8 ppv2-h-8 ppv2-rounded-lg hover:ppv2-bg-black hover:ppv2-bg-opacity-[5%] ppv2-flex ppv2-items-center ppv2-justify-center"
                        >
                            <Ellipsis :size="18" />
                        </button>
                    </template>
                </DropdownMenu>
            </div>
        </td>

        <!-- Days Cells Loop -->
        <td
            v-for="(day, index) in daysOfWeek"
            :key="index"
            class="ppv2-border-r ppv2-border-b ppv2-border-gray-100 ppv2-p-0 ppv2-relative ppv2-h-[72px] group/cell ppv2-cursor-pointer"
        >
            <!-- CELL WITH VALUE -->
            <div
                v-if="getDayData(item, index) && formatDayContent(getDayData(item, index)) !== '-'"
                class="ppv2-group/cell ppv2-w-full ppv2-h-full ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-text-center ppv2-p-2 ppv2-border-[1.5px] ppv2-border-white ppv2-relative"
                :style="getCellStyle(getDayData(item, index), index)"
            >
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2">
                    <span
                        class="ppv2-text-xs ppv2-text-black ppv2-whitespace-pre-line ppv2-leading-relaxed"
                        v-html="formatDayContent(getDayData(item, index)).replace(/\n/g, '<br/>')"
                    ></span>

                    <Tooltip :text="$t('staff_planning_v3.row.edit_shift')" position="top">
                        <button
                            class="ppv2-text-black ppv2-hidden group-hover/cell:ppv2-block ppv2-pointer-events-none group-hover/cell:ppv2-pointer-events-auto ppv2-transition-all duration-200 hover:ppv2-scale-110"
                            @click="$emit('edit', { item, index })"
                        >
                            <Edit :size="12" />
                        </button>
                    </Tooltip>

                    <Tooltip :text="$t('staff_planning_v3.row.delete_shift')" position="top">
                        <button
                            class="ppv2-text-black ppv2-hidden group-hover/cell:ppv2-block ppv2-pointer-events-none group-hover/cell:ppv2-pointer-events-auto ppv2-transition-all duration-200 hover:ppv2-scale-110"
                            @click="$emit('delete', { item, index })"
                        >
                            <Trash2 :size="12" />
                        </button>
                    </Tooltip>
                </div>
                <span class="ppv2-text-xs ppv2-text-black ppv2-leading-relaxed ppv2-mt-1">
                    {{ getDayData(item, index).note }}
                </span>
            </div>

            <!-- EMPTY CELL -->
            <div v-else class="ppv2-group/cell ppv2-w-full ppv2-h-full ppv2-relative ppv2-bg-white ppv2-bg-opacity-[3%]">
                <div
                    class="ppv2-absolute ppv2-inset-0 group-hover/cell:ppv2-opacity-100 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3 ppv2-transition-opacity group-hover/cell:ppv2-bg-[#6344ff0c] ppv2-backdrop-blur-[1px] ppv2-z-10"
                >
                    <div class="ppv2-relative ppv2-inline-block ppv2-group/btn">
                        <Tooltip :text="$t('staff_planning_v3.row.create_shift')" position="top">
                            <button
                                class="ppv2-opacity-0 ppv2-pointer-events-none group-hover/cell:ppv2-opacity-100 group-hover/cell:ppv2-pointer-events-auto ppv2-transition-all duration-200 hover:ppv2-scale-110 ppv2-w-5 ppv2-h-5 ppv2-rounded-full ppv2-bg-[#8B5CF6] ppv2-text-white ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-sm ppv2-leading-none hover:ppv2-bg-[#7c3aed] ppv2-transition-colors"
                                @click="$emit('create-shift', { item, index })"
                            >
                                <Plus :size="16" />
                            </button>
                        </Tooltip>
                    </div>
                    <div class="ppv2-relative ppv2-inline-block ppv2-group/btn">
                        <Tooltip :text="$t('staff_planning_v3.row.add_absence')" position="top">
                            <button
                                class="ppv2-opacity-0 ppv2-pointer-events-none group-hover/cell:ppv2-opacity-100 group-hover/cell:ppv2-pointer-events-auto ppv2-transition-all duration-200 hover:ppv2-scale-110 ppv2-w-8 ppv2-h-8 ppv2-rounded-full ppv2-bg-white dark:ppv2-bg-dark ppv2-text-black ppv2-flex ppv2-items-center ppv2-justify-center ppv2-shadow-sm ppv2-border ppv2-border-[#F3E8FF] hover:ppv2-border-[#8B5CF6] ppv2-transition-colors"
                                @click="$emit('create-absence', { item, index })"
                            >
                                <Ban :size="16" />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </td>

        <!-- Total Hours Cell -->
        <td class="ppv2-border-b ppv2-border-gray-200 ppv2-text-center ppv2-text-sm ppv2-text-black ppv2-h-[72px]">
            {{ item.total }}
        </td>
    </tr>
</template>

<script>
import { Edit, Trash2, Plus, Ban, Ellipsis } from 'lucide-vue' // Adaptez selon votre bibliothèque d'icônes
import Tooltip from '../../../Shared/Tooltip.vue'
import DropdownMenu from './DropdownMenu.vue'

export default {
    name: 'PlanningRow',
    components: { Edit, Trash2, Plus, Ban, Ellipsis, Tooltip, DropdownMenu },
    data() {
        return {
            isMenuOpen: false,
        }
    },

    props: {
        item: { type: Object, required: true },
        daysOfWeek: { type: Array, required: true },
        menuItems: { type: Array, default: () => [] },
        getDayData: { type: Function, required: true },
        formatDayContent: { type: Function, required: true },
        getCellStyle: { type: Function, required: true },
    },

    methods: {
        capitalizeFirst(str) {
            if (!str) return ''
            return str.charAt(0).toUpperCase() + str.slice(1)
        },
    },
}
</script>
<style scoped>
table:not([cellpadding]) td {
    padding: 0 !important;
}
</style>
